
import { defineComponent, ref, onMounted, computed } from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import axios from "@/axios-instance"
import moment from "moment"
import Permission from "@/core/config/Permission";
import { debounce } from "debounce";
import { statusTagDefault } from "@/components/customers/ProfileDetailInfor.vue";
import { titleCaseName } from "@/core/helpers/format";

interface ICustomer {
  id: string;
  method: string;
  profileCode: string;
  fullName: string;
  date: string;
  checkbox: boolean;
}
interface IPagination {
  currentPage: number;
  total: number;
  limit: number;
}
export default defineComponent({
  name: "customers-listing",
  components: {
    Datatable,
  },
  data() {
    return {
      checkDefault: false,
      loading: false,
    }
  },
  watch: {
    search: debounce(async function (this) {
      this.searchItems()
    }, 300)
  },
  methods: {
    checkAll(value) {
      this.checkDefault = value
      this.checkedCustomers = []
      if(!value) {
        return
      }
      const el = document.querySelectorAll('.form-auto-check') 
      el.forEach( e =>{
        let ele =  e as HTMLInputElement
        this.checkedCustomers.push(ele.value as never)
      });
    },
    // Hỏi xác nhận trước khi khôi phục (đồng bộ DS ứng viên đã xóa)
    _confirmRestore(count: number) {
      return Swal.fire({
        text: `Khôi phục ${count} hồ sơ đã chọn? Hồ sơ trở lại danh sách hồ sơ đăng ký xét tuyển.`,
        icon: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Khôi phục",
        cancelButtonText: "Trở lại",
        customClass: { confirmButton: "btn btn-primary order-2", cancelButton: "btn btn-light order-1" },
      });
    },
    _restoreError(response: any) {
      Swal.fire({
        text: response?.data?.errorMessage || "Khôi phục thất bại, vui lòng thử lại.",
        icon: "error", buttonsStyling: false, confirmButtonText: "Đóng", customClass: { confirmButton: "btn btn-primary" },
      });
    },
    // FIX 2026-07: bản cũ xóa dòng khỏi bảng TRƯỚC khi gọi API (không await/không catch) + setTimeout
    // báo "thành công" bất kể kết quả — API lỗi vẫn báo thành công, F5 hồ sơ hiện lại.
    async restoreMutilAdmission() {
      const { isConfirmed } = await this._confirmRestore(this.checkedCustomers.length);
      if (!isConfirmed) { return }
      const ids = JSON.parse(JSON.stringify(this.checkedCustomers));
      await axios.put(`/admission/register/update/ids`, { ids, isDelete: false })
        .then(() => {
          ids.forEach((item) => { this.deleteCustomer(item); }); //gỡ khỏi bảng SAU khi API thành công
          this.checkedCustomers = []
          Swal.fire({ text: `Đã khôi phục ${ids.length} hồ sơ.`, icon: "success", buttonsStyling: false, timer: 1800, timerProgressBar: true, showConfirmButton: false });
        })
        .catch(({ response }) => { this._restoreError(response) });
      this.checkDefault = false
    },
    async restoreAdmission(record) {
      if(record.isDeleteOfStudent){
        Swal.fire({
            text: "Vui lòng khôi phục ứng viên trước khi khôi phục hồ sơ này!",
            icon: "info",
            buttonsStyling: false,
            confirmButtonText: "Đóng",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        return
      }
      const { isConfirmed } = await this._confirmRestore(1);
      if (!isConfirmed) { return }
      await axios.put(`/admission/register/update/ids`, { ids: [record.id], isDelete: false })
        .then(() => {
          this.deleteCustomer(record.id);
          this.checkedCustomers = this.checkedCustomers.filter(item => record.id != item)
          Swal.fire({ text: "Đã khôi phục hồ sơ.", icon: "success", buttonsStyling: false, timer: 1800, timerProgressBar: true, showConfirmButton: false });
        })
        .catch(({ response }) => { this._restoreError(response) });
    },
    async getData(page = 1, limit = 50) {
      this.tableData = []
      this.initCustomers = []
      this.loading = true
      
      await axios.get(`/admission/register/filter/deleted`)
      .then(( {data} ) => {
        data.adRegs.forEach(item => {
          let temp = {
            id: item.id,
            idStudent: item.idStudent,
            method: item.adMethodName,
            profileCode: item.profileCode,
            // Title Case hiển thị (display-only — khôi phục chỉ gửi id, không lưu ngược tên)
            fullName: titleCaseName(item.fullName),
            idCard: item.idCard,
            isDeleteOfStudent:item.isDeleteOfStudent,
            // Hồ sơ thuộc ứng viên ĐÃ XÓA -> tô NỀN XÁM nhẹ (bị khóa, không xử lý trực tiếp; giữ chữ rõ)
            _rowClass: item.isDeleteOfStudent ? 'umt-row-muted' : '',
            majors: item.majors.map(i => {return {name:i.name,priority:i.priority}}).sort((a, b) => {
              if( a.priority !== undefined && b.priority !== undefined ) {
                return a.priority > b.priority ? 1 : -1;
              } else {
                return a.priority !== undefined ? 1 : -1
              }
            }),
            // Ngày XÓA = updatedAt (soft-delete cập nhật updatedAt); trước hiển thị createdAt gây hiểu nhầm
            date: item.updatedAt ? moment(item.updatedAt).format('DD/MM/YYYY') : moment(item.createdAt).format('DD/MM/YYYY'),
            checkbox: true,
            status: item.status,
            reason: item.lastModifiedBy?item.lastModifiedBy.split('|')[1]:'',
            deletedBy: item.lastModifiedBy?item.lastModifiedBy.split('_')[0]:'',
          }
          this.tableData.push(temp)
        })
        // Sắp xếp theo Tên rồi CCCD (locale VN)
        this.tableData.sort((a: any, b: any) => {
          const n = String(a.fullName || '').localeCompare(String(b.fullName || ''), 'vi')
          return n !== 0 ? n : String(a.idCard || '').localeCompare(String(b.idCard || ''), 'vi')
        })
        this.initCustomers.splice(0, this.tableData.length, ...this.tableData);
      })
      .catch(({ response }) => {
        console.log('err',response)
      });
      this.loading = false
    },
    currentChange(currentPage,limit = 0) {
       if(limit == 0) {
        limit = this.pagination.limit
      }
      else {
        this.pagination.limit = limit
      }
      this.getData(currentPage,this.pagination.limit)
    }
  },
  async created() {
    await this.getData()
    //Search toàn cục điều hướng tới với ?q= -> prefill ô tìm SAU khi data về (watch search sẽ tự lọc);
    //đọc từ location thay vì this.$route (hybrid component không có type $route -> TS2339)
    const q = new URLSearchParams(window.location.search).get('q')
    if (q) this.search = q
  },
  setup() {
    const pagination = ref<IPagination>({
      currentPage: 1,
      total: 0,
      limit: 25,
    });
    const checkedCustomers = ref([]);
    const tableHeader = ref([
      {
        key: "checkbox",
        sortable: false,
        class:"d-flex",
        method: "checkall",
      },
      //Title đồng bộ chuẩn list (CSS .umt-table tự uppercase; bỏ "kHÔI PHỤC" gõ lỗi — cột action để TRỐNG)
      {
        name: "Mã hồ sơ",
        key: "profileCode",
        sortable: true,
        class: "min-w-100px text-nowrap",
      },
      {
        name: "Họ tên / CCCD",
        key: "fullName",
        sortable: true,
        class: "min-w-175px text-nowrap",
      },
      {
        name: "Phương thức",
        key: "method",
        sortable: true,
        class: "min-w-100px",
      },
      {
        name: "Ngành",
        key: "majors",
        sortable: false, //mảng ngành — sort chuỗi vô nghĩa
        class: "min-w-200px",
      },
      {
        name: "Ngày xóa",
        key: "date",
        sortable: false,
        class: "text-nowrap",
      },
      {
        name: "Người xóa",
        key: "deletedBy",
        sortable: false,
        class: "min-w-100px",
      },
      {
        name: "Lý do xóa",
        key: "reason",
        sortable: false,
        class: "min-w-150px",
      },
      {
        name: "",
        key: "actions",
        sortable: false,
        class: "text-center w-50px"
      },
    ]);
    const tableData = ref<Array<ICustomer>>([]);
    const initCustomers = ref<Array<ICustomer>>([]);
    const statusTag =  statusTagDefault
    onMounted(async () => {
       //getData()
      //initCustomers.value.splice(0, tableData.value.length, ...tableData.value);
      //console.log(tableData.value)
    });
    const deleteCustomer = async (id) => {
      for (let i = 0; i < tableData.value.length; i++) {
        if (tableData.value[i].id === id) {
          tableData.value.splice(i, 1);
        }
      }
      for (let i = 0; i < initCustomers.value.length; i++) {
        if (initCustomers.value[i].id === id) {
          initCustomers.value.splice(i, 1);
          return
        }
      }
    };
    const search = ref<string>("");
    // Lọc theo Người xóa (client-side trên danh sách đã tải) — '' = tất cả
    const filterDeletedBy = ref<string>("");
    const deletedByOptions = computed(() => Array.from(new Set((initCustomers.value as any[]).map((i: any) => i.deletedBy).filter(Boolean))).sort());
    const searchItems = () => {
      let rows = [...initCustomers.value] as Array<ICustomer>;
      if (filterDeletedBy.value) rows = rows.filter((i: any) => i.deletedBy === filterDeletedBy.value);
      if (search.value !== "") rows = rows.filter((i: any) => searchingFunc(i, search.value));
      tableData.value.splice(0, tableData.value.length, ...rows);
    };

    const searchingFunc = (obj, value): boolean => {
      obj = JSON.parse(JSON.stringify(obj))
      obj.status = statusTag[obj.status]?.label
      for (let key in obj) {
        if (obj[key] && (typeof obj[key] === "string")) {
          if (obj[key].toUpperCase().indexOf(value.toUpperCase()) != -1) {
            return true;
          }
        }
      }
      return false;
    };

    return {
      tableData,
      initCustomers,
      tableHeader,
      deleteCustomer,
      search,
      filterDeletedBy,
      deletedByOptions,
      searchItems,
      checkedCustomers,
      pagination,
      statusTag,
      Permission,
    };
  },
});
